import { Component, OnInit, Type } from '@angular/core';
import {
  AbstractControl,
  ReactiveFormsModule,
  ValidationErrors,
  ValidatorFn,
} from '@angular/forms';
import { MatError, MatFormField, MatLabel, MatSuffix } from '@angular/material/form-field';
import {
  FieldType,
  FieldTypeConfig,
  FormlyFieldConfig,
  FormlyFieldProps,
  FormlyModule,
} from '@ngx-formly/core';
import {
  maxCircumferenceValidator,
  minCircumferenceValidator,
  minMaxCircumferenceValidator,
} from './belt-circumference-validators';
import { BeltCircumferenceComponent } from './belt-circumference.component';

export interface BeltCircumferenceProps extends FormlyFieldProps {
  maxRatio: number;
  defaultMaxRatio: number;
}

export interface FormlyBeltCircumferenceFieldConfig extends FormlyFieldConfig<BeltCircumferenceProps> {
  type: 'belt-circumference' | Type<FormlyBeltCircumferenceComponent>;
}

@Component({
  imports: [
    BeltCircumferenceComponent,
    ReactiveFormsModule,
    FormlyModule,
    MatError,
    MatFormField,
    MatLabel,
    MatSuffix,
  ],
  selector: 'vectronic-formly-belt-circumference-wrapper',
  template: `
    <mat-form-field>
      <mat-label>{{ label }}</mat-label>
      <vectronic-formly-belt-circumference
        [formControl]="formControl"
        [min]="props.min ?? 1"
        [maxRatio]="props.maxRatio ?? 1.25"
        [defaultMaxRatio]="props.defaultMaxRatio ?? 1.2"
        [required]="props.required"
        [id]="id"
      ></vectronic-formly-belt-circumference>
      <span matTextSuffix>cm</span>
      <mat-error>
        <formly-validation-message [field]="field"></formly-validation-message>
      </mat-error>
    </mat-form-field>
  `,
  styles: `
    mat-form-field {
      width: 100%;
    }
    span[mattextsuffix] {
      margin-left: 0.5rem;
    }
  `,
})
export class FormlyBeltCircumferenceComponent
  extends FieldType<FieldTypeConfig<BeltCircumferenceProps>>
  implements OnInit
{
  ngOnInit(): void {
    this.formControl.addValidators([
      this.requiredValidator,
      minCircumferenceValidator(this.min),
      minMaxCircumferenceValidator,
      maxCircumferenceValidator(this.min, this.maxRatio),
    ]);
  }

  private requiredValidator: ValidatorFn = (control: AbstractControl): ValidationErrors | null => {
    const value = control.getRawValue();
    if (value == null || value.min == null || value.max == null) {
      return { required: { message: 'This field is required' } };
    }
    return null;
  };

  get label() {
    return this.props?.label || 'Belt circumference';
  }

  min = () => this.props?.min;

  maxRatio = () => this.props?.maxRatio ?? 1.25;
}
